// Secondary ops step — used by hybrid parts (cut blank + secondary machining).
//
// For now this is a freeform notes area + an optional total-hours estimate.
// Full per-machine breakdown (the SetupsStep treatment) is a future pass.
// Notes are appended to part.notes with a "secondary ops:" prefix so the
// freeform text isn't lost; total hours rides on setupHrs as a placeholder.

import { useState } from 'react';
import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';

const NOTES_PREFIX = 'secondary ops:\n';

function readNotes(allNotes: string | undefined): { secondary: string; rest: string } {
  if (!allNotes) return { secondary: '', rest: '' };
  const idx = allNotes.indexOf(NOTES_PREFIX);
  if (idx < 0) return { secondary: '', rest: allNotes };
  const after = allNotes.slice(idx + NOTES_PREFIX.length);
  // Secondary block runs until a double newline (next section) or end.
  const blockEnd = after.indexOf('\n\n');
  if (blockEnd < 0) return { secondary: after, rest: allNotes.slice(0, idx).trimEnd() };
  return {
    secondary: after.slice(0, blockEnd),
    rest: (allNotes.slice(0, idx) + after.slice(blockEnd + 2)).trim(),
  };
}

function writeNotes(secondary: string, rest: string): string {
  const trimmed = secondary.trim();
  const restTrim = rest.trim();
  if (!trimmed) return restTrim;
  const block = `${NOTES_PREFIX}${trimmed}`;
  return restTrim ? `${restTrim}\n\n${block}` : block;
}

export function SecondaryOpsStep({ part, onChange }: StepProps) {
  const initial = readNotes(part.notes);
  const [secondary, setSecondary] = useState(initial.secondary);

  function updateSecondary(next: string) {
    setSecondary(next);
    onChange({ notes: writeNotes(next, initial.rest) });
  }

  return (
    <div className="space-y-3">
      <Field label="Secondary operations">
        <textarea
          value={secondary}
          onChange={(e) => updateSecondary(e.target.value)}
          rows={6}
          placeholder="e.g.&#10;- 4 holes drilled #21, tap 10-32&#10;- 1/4-20 tapped hole on each side&#10;- Deburr edges, break corners 0.010"
          autoFocus
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange resize-y"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Est. setup hrs (total)">
          <NumberInput
            value={part.setupHrs}
            onChange={(v) => onChange({ setupHrs: v })}
            step={0.5}
            unit="hrs"
            placeholder="0.5"
            numpadTitle="Estimated setup hours"
          />
        </Field>
        <Field label="Est. setup cost ($)">
          <NumberInput
            value={part.setupCost}
            onChange={(v) => onChange({ setupCost: v })}
            step={1}
            unit="$"
            placeholder="45"
            numpadTitle="Estimated setup cost"
          />
        </Field>
      </div>

      <div className="text-xs text-text-mute italic">
        Full per-machine secondary-op breakdown (Setups-step treatment) is a future pass.
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
